import {
  InstallmentSettleFormValues,
  InstallmentSettlePartiallyRequest,
  SettleInstallmentSingleData,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { Button, Form, Modal, ModalProps } from 'antd';
import moment from 'moment';
import React, { useEffect } from 'react';
import SettlePartiallyAccountPayableForm from './SettlePartiallyAccountPayableForm';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useInstallmentSettledPartially } from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import { DateTimeFormat } from '@/utils/moment';
import { handleInstallmentAlreadyEdited } from '@/core/domain/financial/AccountPayable/AccountPayable.errors';
import { AxiosError } from 'axios';

interface Props extends Omit<ModalProps, 'onCancel'> {
  data: SettleInstallmentSingleData;
  onCancel: VoidFunction;
}

const SettledPartiallyModal: React.FC<Props> = ({ onCancel, open, data }) => {
  const [form] = Form.useForm<InstallmentSettleFormValues>();

  const settlePartiallyMutation = useInstallmentSettledPartially();

  const today = moment().format('YYYY-MM-DD');
  const payDate = Form.useWatch('payDate', form);

  const isEarlierDate = moment(payDate).isBefore(today);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        dueDate: moment().add(1, 'months'),
        payDate: moment(),
      });
    }
  }, [data]);

  function handleCancel() {
    form.resetFields();
    onCancel();
  }

  const handleSettlePartiallyInstallment = async () => {
    try {
      const values = await form.validateFields();

      if (data && data.id && data.installmentPayableId) {
        const obj: InstallmentSettlePartiallyRequest = {
          ...values,
          id: data.id,
          installmentPayableId: data.installmentPayableId,
          netValue: (data.installmentValue ?? 0) + (data.discountIncrease ?? 0),
          dueDate: values.dueDate.format(DateTimeFormat),
        };

        await settlePartiallyMutation.mutateAsync(obj);

        handleCancel();
      }
    } catch (err: unknown) {
      const error = err as AxiosError;

      if (error.response?.data) {
        if (handleInstallmentAlreadyEdited(error)) {
          handleCancel();
        }
      }
    }
  };

  return (
    <Modal
      width={400}
      open={open}
      onCancel={handleCancel}
      title={'Liquidar Parcela Parcialmente'}
      footer={[
        <Button type="primary" ghost onClick={onCancel}>
          Cancelar
        </Button>,
        <Button
          disabled={
            isEarlierDate &&
            !hasPermissions([
              Authorities.FINANCIAL_ACCOUNT_PAYABLE_SETTLE_REFUND_WITH_PREVIOUS_DATE,
            ])
          }
          type="primary"
          loading={settlePartiallyMutation.isLoading}
          onClick={handleSettlePartiallyInstallment}
        >
          Liquidar
        </Button>,
      ]}
      destroyOnClose
    >
      <SettlePartiallyAccountPayableForm form={form} data={data} />
    </Modal>
  );
};

export default SettledPartiallyModal;
